import type { SwitchRef } from "@/api/access/access.types";
export type ModuleKey = (typeof MODULE_KEYS_MAP)[keyof typeof MODULE_KEYS_MAP];

export type AccessResult = {
  view: boolean;
  create: boolean;
  edit: boolean;
};
export type PermissionAction = "view" | "create" | "edit";

export type ModulePermissions = {
  permissions: {
    view: boolean;
    create: boolean;
    edit: boolean;
  };
};

export type PermissionsResponse = {
  data: Record<ModuleKey, ModulePermissions>;
  success: boolean;
};

export type ModulePermissionsDetail = {
  data: Record<string, { view: boolean; create: boolean; edit: boolean }>;
  success: boolean;
};
// Example shape:
// {
//   "service-providers": { permissions: { view: false, create: true, edit: false } },
//   "work-orders":       { permissions: { view: true,  create: true, edit: true  } }
// }

export const routeKeys = {
  home: "",
  myTasks: "my-tasks",
  spCalendar: "sp-calendar",
  reports: "reports",
  chat: "chat",
  // SERV-3232: the roles matrix grants email_ai; the backend route key is "email-ai".
  emailAi: "email-ai",
  checkInOut: "check-in-out",
  formBuilder: "form-builder",
  dispatchQueue: "dispatch-queue",

  entities: "entities",
  clients: "clients",
  clientAlignment: "client-alignment",
  locations: "locations",
  serviceProviders: "service-providers",
  workOrders: "work-orders",

  assets: "assets",
  procurement: "procurement",
  proposals: "proposals",
  fmIntegrations: "fm-integrations",

  accountsPayable: "accounts-payable",
  w9Requests: "w9-requests",
  closeOut: "close-out",
  accountsReceivable: "accounts-receivable",
  files: "files",

  rolesPermissions: "roles-permissions",
  userManagement: "user-management",
  settings: "settings",
} as const;

export const requiredModuleByPath: Record<string, ModuleKey | null> = {
  "/": routeKeys.home,
  "/my-tasks": routeKeys.myTasks,
  "/sp-calendar": routeKeys.spCalendar,
  "/reports": routeKeys.reports,
  "/chat": "chat",
  "/email-ai": routeKeys.emailAi,
  "/check-in-out": routeKeys.workOrders,
  // Form Builder module (templates + assignments) is guarded like /form-manager.
  "/form-builder": routeKeys.settings,
  "/dispatch-queue": routeKeys.dispatchQueue,
  // SERV-3137: v1 of the live map reuses the Dispatch Queue permission rather than
  // seeding a new module - see the ticket's own scope note.
  "/live-map": routeKeys.dispatchQueue,
  "/entities": routeKeys.entities,
  "/clients": routeKeys.clients,
  // Team Assignments lives under Clients but is its own module (SERV-3236), so it needs an
  // exact entry: the base-segment fallback would gate it on the Clients permission.
  "/clients/team-assignments": routeKeys.clientAlignment,
  "/locations": routeKeys.locations,
  "/service-providers": routeKeys.serviceProviders,
  "/work-orders": routeKeys.workOrders,

  "/assets": routeKeys.assets,
  "/procurement": routeKeys.procurement,
  "/proposals": routeKeys.proposals,
  // SE-global superadmin area: super_admin passes the guard early; any other
  // user type resolves this module to no-access and is redirected home.
  "/fm-integrations": routeKeys.fmIntegrations,

  "/accounts-payable": routeKeys.accountsPayable,
  // W-9 Requests lives under the Service Providers path but is its own module,
  // so it needs an exact entry: the base-segment fallback would resolve it to
  // "/service-providers" and gate it on the wrong permission.
  "/service-providers/w9-requests": routeKeys.w9Requests,
  "/close-out": routeKeys.closeOut,
  "/accounts-receivable": routeKeys.accountsReceivable,
  "/files": routeKeys.files,

  "/roles-permissions": routeKeys.rolesPermissions,
  "/user-management": routeKeys.userManagement,
  "/settings": routeKeys.settings,
  "/form-manager": routeKeys.settings,
} as const;

/**
 * Contract 7.4: the pages whose route the engine's switch decides. A path here is checked against
 * the effective access instead of the module list; every other path keeps the module check.
 */
export const requiredSwitchByPath: Record<string, SwitchRef> = {
  "/work-orders": { key: "work_order", action: "view" },
  "/check-in-out": { key: "check_in_out", action: "view" },
  "/roles-permissions": { key: "roles_permissions", action: "view" },
  "/user-management": { key: "user_management", action: "view" },
  // UI25: an exact entry, since the base-segment fallback would open New User on view, and its
  // create asks user_management:create.
  "/user-management/add-user": { key: "user_management", action: "create" },
};

export const MODULE_KEYS_MAP = {
  home: "",
  myTasks: "my-tasks",
  spCalendar: "sp-calendar",
  reports: "reports",
  chat: "chat",
  // SERV-3232: the roles matrix grants email_ai; the backend route key is "email-ai".
  emailAi: "email-ai",
  checkInOut: "check-in-out",
  formBuilder: "form-builder",
  dispatchQueue: "dispatch-queue",
  clients: "clients",
  clientAlignment: "client-alignment",
  locations: "locations",
  serviceProviders: "service-providers",
  workOrders: "work-orders",
  workOrder: "work_order",
  assets: "assets",
  procurement: "procurement",
  proposals: "proposals",
  fmIntegrations: "fm-integrations",
  accountsPayable: "accounts-payable",
  w9Requests: "w9-requests",
  closeOut: "close-out",
  accountsReceivable: "accounts-receivable",
  files: "files",
  rolesPermissions: "roles-permissions",
  userManagement: "user-management",
  settings: "settings",
  entities: "entities",
  brands: "brands",
  visit: "visit",
  alignment: "alignment",
} as const;
